import CloseButton from '@atoms/CloseButton/CloseButton';
import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import { IBannerNotification } from '@models/IBannerNotification';
import { IMiniBasketItem } from '@models/IMiniBasket';
import IconSizeEnum from '@models/enums/IconSizeEnum';
import ProductStockStatusEnum from '@models/enums/ProductStockStatusEnum';
import classNames from 'classnames';
import { motion } from 'framer-motion';

interface Props extends IMiniBasketItem {
  readonly onRemoveItemFromCart: (code: string, isGift?: boolean) => void;
  readonly giftItemNotification?: IBannerNotification;
  readonly dataTestId: string;
}

const MiniBasketItem = ({
  amountLabels,
  code,
  displayName,
  dataTestId,
  discountedPrice,
  giftItemNotification,
  isFreeItem,
  lineItemTag,
  quantityLabel,
  stockStatus,
  stockStatusLabel,
  selectedQuantity,
  thumbnail,
  url,
  onRemoveItemFromCart,
}: Props) => (
  <motion.div
    className="relative rounded-[10px] border border-grey bg-white p-2.5"
    layout
    transition={{
      type: 'spring',
      duration: 0.3,
      layout: { type: 'spring', duration: 0.7 },
    }}
    animate={{ scale: 1, opacity: 1 }}
    exit={{ scale: 0.8, opacity: 0 }}
    data-testid={dataTestId}
  >
    <div className="flex gap-5">
      <a
        href={url}
        target="_blank"
        className="max-h-[142px] max-w-[142px]"
        rel="noreferrer"
      >
        <Image
          image={thumbnail}
          imageClasses="object-contain h-full bg-grey-100 rounded-[10px]"
          dataTestId={`${dataTestId}-Image`}
        />
      </a>

      <div className="flex w-full flex-col justify-between gap-y-2.5 py-2.5">
        <div>
          <p
            className={classNames('body-four mb-[5px] text-grey-600 ', {
              'text-red-100': stockStatus === ProductStockStatusEnum.OutOfStock,
            })}
            data-testid={`${dataTestId}-Stock-Status-Label`}
          >
            {stockStatusLabel}
          </p>
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-block max-w-[80%]"
          >
            <p
              className="body-two line-clamp-2 w-full break-words"
              data-testid={`${dataTestId}-Name`}
            >
              {displayName}
            </p>
          </a>
        </div>

        <div className="flex w-full justify-between">
          <p
            className="body-three mt-auto pb-1.5"
            data-testid={`${dataTestId}-Quantity`}
          >
            {`${quantityLabel}  ${selectedQuantity}`}
          </p>

          <div>
            <p
              className="body-one text-right"
              data-testid={`${dataTestId}-Price`}
            >
              {discountedPrice}
            </p>

            {amountLabels
              ?.filter((amountLabel) => amountLabel)
              .map((amountLabel, index) => (
                <p
                  key={amountLabel}
                  className="body-four text-right text-grey-600 "
                  data-testid={`${dataTestId}-Amount-Label-${index}`}
                >
                  {amountLabel}
                </p>
              ))}
          </div>
        </div>
      </div>
    </div>
    {lineItemTag && (
      <div className="w-full pt-2.5">
        <div
          className="flex items-center justify-center gap-x-[6px] rounded-[100px] bg-grey-100"
          data-testid={`${dataTestId}-Refurbished-Tag`}
        >
          <Icon
            name={lineItemTag.icon}
            colorClassName="text-yellow"
            size="16px"
          />

          <p className="body-four">{lineItemTag.label}</p>
        </div>
      </div>
    )}
    {isFreeItem && giftItemNotification?.text && (
      <div className="w-full pt-2.5">
        <div
          className="flex items-center justify-center gap-x-1.5 rounded-[100px] bg-yellow px-3 py-0.5"
          data-testid={`${dataTestId}-Gift-Item-Tag`}
        >
          {giftItemNotification.icon && (
            <Icon
              name={giftItemNotification.icon}
              colorClassName="text-black"
              size="16px"
            />
          )}

          <p
            className="body-four"
            dangerouslySetInnerHTML={{ __html: giftItemNotification.text }}
          />
        </div>
      </div>
    )}
    {/* // TODO: Revert this conditional ONLY when gift items are able to
      // be removed from the basket which currently Opti does not allow */}
    {!isFreeItem && (
      <CloseButton
        className="absolute top-4 right-4"
        size={IconSizeEnum.md}
        onClick={() => {
          onRemoveItemFromCart(code, isFreeItem);
        }}
        dataTestId={`${dataTestId}-Close-Button`}
      />
    )}
  </motion.div>
);

export default MiniBasketItem;
